import React, { useCallback, useMemo, useState } from 'react';
import { useUIContext } from './contexts/UIContext';
import { useAuthContext } from './contexts/AuthContext';
import { useProgressContext } from './contexts/ProgressContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import BaseModal from './components/modals/BaseModal';
import ParentalGate from './components/modals/ParentalGate';
import InstantUnlockModal from './components/modals/InstantUnlockModal';
import PaywallModal from './components/modals/PaywallModal';
import CliffhangerPaywallModal from './components/modals/CliffhangerPaywallModal';
import TrialExpiredModal from './components/modals/TrialExpiredModal';
import StorybookUpsellModal from './components/modals/StorybookUpsellModal';
import StreamlinedUnmuteModal from './components/modals/StreamlinedUnmuteModal';
import WelcomeGiftModal from './components/modals/WelcomeGiftModal';
import FeedbackBar from './components/common/FeedbackBar';
import RewardModal from './components/modals/RewardModal';
import ConfirmationModal from './components/modals/ConfirmationModal';
import ProfilePickerModal from './components/modals/ProfilePickerModal';
import { appData } from './data';
import { trackEvent } from './api/firebase';
import { hapticFeedback } from './utils/haptics';

const ModalManager = () => {
    const {
        page, setPage, showFeedback, triggerParentalGate, gateSuccessAction, isGateOpen, setIsGateOpen,
        isUnlockModalOpen, setUnlockModalOpen,
        isPaywallOpen, setPaywallOpen,
        isCliffhangerPaywallOpen, setCliffhangerPaywallOpen,
        isTrialExpiredModalOpen, setTrialExpiredModalOpen,
        isStorybookUpsellOpen, setStorybookUpsellOpen,
        isStreamlinedUnmuteOpen, setStreamlinedUnmuteOpen,
        showWelcomeModal, setShowWelcomeModal,
        feedback, reward, setReward, setActiveModule,
        isSwitchProfileModalOpen, setIsSwitchProfileModalOpen, isProfilePickerModalOpen, setIsProfilePickerModalOpen, profileToSwitchTo,
        paywallContext, setPaywallContext, setReturnToUnit, audioManager
    } = useUIContext();

    const { user } = useAuthContext();
    const { enableAllSounds, updateLearningSettings, activeProfile, progress, setCurrentActiveProfile, clearInProgressModule } = useProgressContext();

    // Loading states for async operations
    const [isSwitchingProfile, setIsSwitchingProfile] = useState(false);
    const [isSwitchingToExplorer, setIsSwitchingToExplorer] = useState(false);

    const handleConfirmSwitch = useCallback(async () => {
        if (profileToSwitchTo) {
            setIsSwitchingProfile(true);
            try {
                // Stop any playing audio when switching profiles
                if (audioManager?.current) {
                    audioManager.current.stopAllSounds();
                }
                await setCurrentActiveProfile(profileToSwitchTo.id);
                showFeedback(`Switched to ${profileToSwitchTo.childName}'s profile!`, 'success');
                setIsSwitchProfileModalOpen(false);
                // Don't change the page - stay on current page
            } catch (error) {
                showFeedback('Could not switch profile.', 'error');
            } finally {
                setIsSwitchingProfile(false);
            }
        }
    }, [profileToSwitchTo, setCurrentActiveProfile, showFeedback, setIsSwitchProfileModalOpen, audioManager]);

    const handleSwitchToExplorer = useCallback(async () => {
        if (!activeProfile) return;
        setIsSwitchingToExplorer(true);
        try {
            await updateLearningSettings('explorer', activeProfile.ageGroup);
            showFeedback("You're in Explorer Mode! All lessons are now unlocked.", "success");
            setUnlockModalOpen(false);
        } catch (error) {
            showFeedback("Could not switch modes. Please try again.", "error");
        } finally {
            setIsSwitchingToExplorer(false);
        }
    }, [activeProfile, updateLearningSettings, showFeedback, setUnlockModalOpen]);
    
    const handleStartStoryFromWelcome = useCallback(() => {
        setShowWelcomeModal(false);
        if (!activeProfile) return;
        const languageData = appData[activeProfile.currentLanguage];
        if (!languageData || !languageData.storybooks) return;
        const storyId = activeProfile.currentLanguage === 'Twi' ? "ananse_and_the_pot_of_wisdom" : "ijapa_and_the_borrowed_feathers";
        const story = languageData.storybooks[storyId];
        if (!story) return;
        setActiveModule({ ...story, id: story.id, title: story.title });
        setPage('storybook');
    }, [activeProfile, setActiveModule, setPage, setShowWelcomeModal]);

    const handleRewardModalClose = useCallback(() => {
        setReward(null);
    }, [setReward]);
    

    return (
        <ErrorBoundary fallbackMessage="An error occurred with the modal system. Please refresh the page.">
            <ParentalGate 
                isOpen={isGateOpen} 
                onClose={() => setIsGateOpen(false)} 
                onSuccess={() => { 
                    setIsGateOpen(false); 
                    if (gateSuccessAction) {
                        gateSuccessAction(); 
                    }
                }} 
            />
            <InstantUnlockModal 
                isOpen={isUnlockModalOpen} 
                onClose={() => setUnlockModalOpen(false)} 
                onConfirmSwitch={handleSwitchToExplorer}
                isLoading={isSwitchingToExplorer}
            />
            <StreamlinedUnmuteModal 
                isOpen={isStreamlinedUnmuteOpen} 
                onClose={() => setStreamlinedUnmuteOpen(false)} 
                onConfirm={() => { 
                    setStreamlinedUnmuteOpen(false); 
                    triggerParentalGate(enableAllSounds, activeProfile); 
                }} 
            />
            <PaywallModal 
                isOpen={isPaywallOpen} 
                onClose={() => { 
                    setPaywallOpen(false); 
                    if (paywallContext) {
                        setPage('learning_path');
                        if (paywallContext.activeModule) {
                            // FIX: Use setReturnToUnit and the .unit property
                            setReturnToUnit(paywallContext.activeModule.unit);
                        }
                        setPaywallContext(null);
                    } else if (page !== 'learning_path') {
                        setPage('learning_path');
                    }
                }} 
                onViewPlans={() => { 
                    setPaywallOpen(false); 
                    setPage('subscription_page'); 
                }} 
            />
            <CliffhangerPaywallModal 
                isOpen={isCliffhangerPaywallOpen} 
                onClose={() => { 
                    setCliffhangerPaywallOpen(false); 
                    setPage('learning_path');
                }} 
                onViewPlans={() => { 
                    setCliffhangerPaywallOpen(false); 
                    setPage('subscription_page'); 
                }} 
                paywallContext={paywallContext}
            />
            <StorybookUpsellModal 
                isOpen={isStorybookUpsellOpen} 
                onUnlock={() => { 
                    setStorybookUpsellOpen(false); 
                    setPage('subscription_page'); 
                }} 
                onClose={() => { 
                    setStorybookUpsellOpen(false); 
                    setPage('dashboard'); 
                }}
            />
            <TrialExpiredModal 
                isOpen={isTrialExpiredModalOpen} 
                onViewPlans={() => { 
                    setTrialExpiredModalOpen(false); 
                    setPage('subscription_page'); 
                }} 
                onClose={() => setTrialExpiredModalOpen(false)} 
            />
            <WelcomeGiftModal 
                isOpen={showWelcomeModal} 
                onClose={() => {
                    setShowWelcomeModal(false);
                    // Keep the session flag true so modal doesn't show again in this session
                    // The flag will reset when the app is refreshed/reloaded
                }} 
                onStartStory={handleStartStoryFromWelcome} 
                userHasSeenPreview={progress?.hasCompletedStoryPreview} 
            />
            <FeedbackBar message={feedback.message} type={feedback.type} onClose={() => showFeedback('', '')} />
            <RewardModal 
                isOpen={!!reward} 
                onClose={handleRewardModalClose} 
                stickerUrl={reward?.stickerUrl} 
                themeTitle={reward?.themeTitle} 
            />
            <ConfirmationModal
                isOpen={isSwitchProfileModalOpen}
                onClose={() => setIsSwitchProfileModalOpen(false)}
                onConfirm={handleConfirmSwitch}
                title="Switch Profile?"
                confirmText="Yes, Switch"
                confirmColor="bg-blue-500"
                isLoading={isSwitchingProfile}
                loadingText="Switching..."
            >
                <p>Set <span className="font-bold">{profileToSwitchTo?.childName}</span> as the active learner?</p>
            </ConfirmationModal>
            
            <ProfilePickerModal
                isOpen={isProfilePickerModalOpen}
                onClose={() => setIsProfilePickerModalOpen(false)}
            />
        </ErrorBoundary>
    );
};

export default ModalManager;
